"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Eye, ShoppingCart, Zap, Minus, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { cn } from "@/lib/utils";
import { useCartStore } from "@/store/cart.store";
import { useCart } from "@/features/cart/hook/useCart";
import { Product } from "@/types/product.types";
import { getOptionPrice } from "@/lib/pricing";

interface ProductCardProps {
    product: Product;
    onQuickView?: (() => void) | undefined;
    className?: string | undefined;
}

export const ProductCard = ({
    product,
    onQuickView,
    className,
}: ProductCardProps) => {
    const items = useCartStore((s) => s.items);
    const { addItem, updateItem } = useCart();

    // Pick the first attribute that actually has options — no hardcoded slugs
    const weightAttribute = product?.attributes?.find(
        (attr) => (attr?.attribute?.options ?? []).length > 0
    );
    const weightOptions = weightAttribute?.attribute?.options ?? [];

    const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
    const [localQuantity, setLocalQuantity] = useState(1);

    const cartItem =
        weightOptions.length === 0
            ? items.find(
                (item) =>
                    item.productId === product.id &&
                    (!item.attributeOptionId || item.attributeOptionId === "")
            )
            : null;

    if (!product) return null;

    const { sku, slug, name, subName, price, images, image, originalPrice, salePrice } =
        product;

    // ── Pricing ──────────────────────────────────────────────────────────────
    const effectiveBase = salePrice ? Number(salePrice) : Number(price || 0);

    const currentPriceDisplay = (() => {
        if (weightOptions.length > 0 && selectedOptionIds.length === 0) {
            const prices = weightOptions.map(o => getOptionPrice(effectiveBase, o));
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            if (minPrice !== maxPrice) {
                return `${minPrice.toLocaleString()} - ${maxPrice.toLocaleString()}`;
            }
            return `${minPrice.toLocaleString()}`;
        }

        if (weightOptions.length > 0) {
            let total = 0;
            for (const id of selectedOptionIds) {
                const option = weightOptions.find((o) => o.id === id);
                if (option) total += getOptionPrice(effectiveBase, option);
            }
            return `${total.toLocaleString()}`;
        }

        return `${getOptionPrice(effectiveBase, null).toLocaleString()}`;
    })();

    const oldPrice = (() => {
        if (!originalPrice || !salePrice) return null;
        if (weightOptions.length > 0 && selectedOptionIds.length === 0) return null;
        
        let oldTotal = 0;
        let currTotal = 0;
        if (weightOptions.length > 0) {
            for (const id of selectedOptionIds) {
                const option = weightOptions.find((o) => o.id === id);
                if (option) {
                    oldTotal += getOptionPrice(Number(originalPrice), option);
                    currTotal += getOptionPrice(effectiveBase, option);
                }
            }
        } else {
            oldTotal = getOptionPrice(Number(originalPrice), null);
            currTotal = getOptionPrice(effectiveBase, null);
        }
        
        return oldTotal !== currTotal ? oldTotal : null;
    })();

    const discountPercentage = (() => {
        const op = Number(originalPrice || 0);
        const sp = Number(salePrice || 0);
        if (!op || !sp || op <= sp) return null;
        return Math.round(((op - sp) / op) * 100);
    })();

    // ── Cart helpers ─────────────────────────────────────────────────────────
    const displayQty = cartItem ? cartItem.quantity : localQuantity;

    const handleAddToCart = async () => {
        if (weightOptions.length > 0 && selectedOptionIds.length === 0) return;
        
        if (selectedOptionIds.length > 0) {
            for (const id of selectedOptionIds) {
                await addItem(product.id, id, 1);
            }
        } else {
            await addItem(product.id, "", localQuantity);
        }
        setLocalQuantity(1);
        setSelectedOptionIds([]);
    };

    const decrementQty = (e: React.MouseEvent) => {
        e.stopPropagation();
        cartItem
            ? updateItem(cartItem.id, cartItem.quantity - 1)
            : setLocalQuantity((q) => Math.max(1, q - 1));
    };

    const incrementQty = (e: React.MouseEvent) => {
        e.stopPropagation();
        cartItem
            ? updateItem(cartItem.id, cartItem.quantity + 1)
            : setLocalQuantity((q) => q + 1);
    };

    return (
        <div
            className={cn(
                "group relative flex flex-col h-full",
                "rounded-2xl border border-primary-50 bg-white",
                // Uniform padding that works from ~180 px card width upwards
                "p-2 sm:p-3 md:p-4",
                "transition-all duration-300 hover:shadow-xl",
                className
            )}
        >
            {/* ── IMAGE ───────────────────────────────────────────────────── */}
            <Link
                href={`/products/${slug}`}
                className="relative aspect-square rounded-xl overflow-hidden block flex-shrink-0"
            >
                {/* SKU */}
                <div className="absolute top-1.5 left-1.5 z-20">
                    <span className="text-[9px] sm:text-[10px] font-bold text-white bg-blue-800 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                        {sku || "EZ-001"}
                    </span>
                </div>

                {/* Discount */}
                {discountPercentage && (
                    <div className="absolute top-1.5 right-1.5 z-20">
                        <div className="flex items-center gap-0.5 text-red-500 bg-white px-1.5 py-0.5 rounded-lg shadow-md font-bold text-[9px] sm:text-[10px]">
                            <Zap className="h-2 w-2 fill-red-500 flex-shrink-0" />
                            <span>{discountPercentage}%</span>
                        </div>
                    </div>
                )}

                <Image
                    src={(() => {
                        if (images && images.length > 0) {
                            const primary = images.find(img => img.isPrimary);
                            return primary ? primary.url : images[0].url;
                        }
                        return image || "/placeholder.png";
                    })()}
                    alt={name}
                    fill
                    sizes="(min-width: 1024px) 20vw, (min-width: 768px) 25vw, (min-width: 640px) 50vw, 70vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                />

                {/* Quick-view — desktop hover only */}
                <div className="absolute inset-0 hidden md:flex items-center justify-center bg-black/5 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                        variant="secondary"
                        size="icon"
                        className="h-10 w-10 rounded-full bg-white text-primary-900"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onQuickView?.();
                        }}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                </div>
            </Link>

            {/* ── NAME ────────────────────────────────────────────────────── */}
            <div className="mt-2 sm:mt-3 flex flex-col gap-0.5 text-center">
                <Link href={`/products/${slug}`}>
                    <h3 className="text-xs sm:text-sm font-bold text-slate-700 hover:text-[#8B6F47] transition-colors line-clamp-2 leading-snug">
                        {name}
                    </h3>
                    {subName && (
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 line-clamp-1 leading-snug">
                            {subName}
                        </p>
                    )}
                </Link>
            </div>

            {/* ── VARIANT / QTY + PRICE ───────────────────────────────────
                Both sides are flex-shrink-0 / min-w-0 so the row never
                wraps and neither side bleeds out of the card.
            ──────────────────────────────────────────────────────────────── */}
            <div className="mt-2 sm:mt-3 mb-2 sm:mb-3 flex items-center justify-between gap-1.5">
                {/* Left: weight selector OR qty stepper — fixed width */}
                {weightOptions.length > 0 ? (
                    <div className="flex flex-wrap gap-1 max-w-[120px] flex-shrink-0">
                        {weightOptions.map((option) => {
                            const isSelected = selectedOptionIds.includes(option.id);
                            return (
                                <Button
                                    key={option.id}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedOptionIds(prev => 
                                            isSelected 
                                                ? prev.filter(id => id !== option.id)
                                                : [...prev, option.id]
                                        );
                                    }}
                                    className={`px-2 py-0.5 text-[10px] sm:text-xs rounded border transition-colors ${
                                        isSelected 
                                            ? "border-primary-600 bg-primary-600 text-primary-50 font-medium" 
                                            : "border-gray-200 text-gray-700 hover:border-gray-400 bg-white"
                                    }`}
                                >
                                    {option.label}
                                </Button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex items-center justify-between border rounded-xl px-1.5 py-1 flex-shrink-0 h-7 sm:h-8 w-[68px] sm:w-[84px] bg-white shadow-sm border-primary-50">
                        <button
                            onClick={decrementQty}
                            disabled={displayQty <= 1}
                            className="text-primary-600 hover:text-primary-900 transition disabled:opacity-30"
                            aria-label="Diminuer la quantité"
                        >
                            <Minus className="h-2.5 w-2.5" />
                        </button>
                        <span className="text-[11px] sm:text-xs font-bold text-primary-900 tabular-nums">
                            {displayQty}
                        </span>
                        <button
                            onClick={incrementQty}
                            className="text-primary-600 hover:text-primary-900 transition"
                            aria-label="Augmenter la quantité"
                        >
                            <Plus className="h-2.5 w-2.5" />
                        </button>
                    </div>
                )}

                {/* Right: price — min-w-0 + whitespace-nowrap so it never wraps */}
                <div className="flex flex-col items-end min-w-0">
                    <span className="text-xs sm:text-sm font-bold text-slate-700 leading-none whitespace-nowrap">
                        {currentPriceDisplay} DA
                    </span>
                    {oldPrice && (
                        <span className="line-through text-red-500 text-[9px] sm:text-[10px] font-semibold leading-none mt-0.5 whitespace-nowrap">
                            {oldPrice.toLocaleString()} DA
                        </span>
                    )}
                </div>
            </div>

            {/* ── CTA ─────────────────────────────────────────────────────
                Button takes all available space; cart icon is a fixed small
                square so it never crowds the label on narrow cards.
            ──────────────────────────────────────────────────────────────── */}
            <div className="flex items-center gap-1.5 sm:gap-2 mt-auto">
                <Link href={`/products/${slug}`} className="flex-1 min-w-0">
                    <Button className="h-8 sm:h-9 w-full rounded-xl text-[10px] sm:text-xs font-semibold px-1 truncate">
                        Acheter maintenant
                    </Button>
                </Link>

                <Button
                    size="icon"
                    className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0 rounded-xl"
                    disabled={weightOptions.length > 0 && selectedOptionIds.length === 0}
                    onClick={
                        cartItem
                            ? () => updateItem(cartItem.id, cartItem.quantity + 1)
                            : handleAddToCart
                    }
                >
                    <ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
            </div>
        </div>
    );
};